/**
 * 수신 그룹 ↔ 주소 입력칸(RecipientAutocomplete) 변환.
 *
 * 왜 별도 파일인가
 *   같은 변환을 편집 다이얼로그(그룹 → 입력칸 → 그룹)와 작성창(그룹 → 입력칸) 두 곳이 쓴다.
 *   한쪽에만 고치면 "편집 화면에서는 이름이 보이는데 작성창에서는 주소만 나오는" 어긋남이 생긴다.
 *
 * RecipientAutocomplete 의 값 모양
 *   freeSolo 라 배열 원소가 두 가지다 — 사용자가 직접 친 문자열, 또는 후보에서 고른 객체({ name, email, ... }).
 *   그래서 양방향 변환이 늘 두 경우를 다 받아야 한다.
 */

/** 자동완성 옵션과 같은 검증식 — 여기가 느슨하면 화면은 통과시키고 서버가 막는다. */
const EMAIL_RE = /^[^\s<>@,;]+@[^\s<>@,;.]+(\.[^\s<>@,;.]+)+$/;

/**
 * 입력칸의 원소 하나 → { name, email }.
 *
 * "홍길동 <a@b.com>" 처럼 이름이 붙은 문자열도 풀어낸다 — 다른 곳에서 복사해 붙여넣는 흔한 형태라,
 * 이름을 버리고 주소만 남기면 그룹에 저장된 뒤 누구인지 알 수 없게 된다.
 * 주소를 못 찾으면 null 을 돌려주고 호출부가 버린다.
 */
export function toAddress(v) {
    if (!v) return null;

    if (typeof v === "object") {
        const email = (v.email || "").trim();
        if (!EMAIL_RE.test(email)) return null;
        const name = (v.name || "").trim();
        return { name: name || null, email };
    }

    const raw = String(v).trim();
    if (!raw) return null;

    const m = raw.match(/^\s*(.*?)\s*<([^>]+)>\s*$/);
    if (m) {
        const email = m[2].trim();
        if (!EMAIL_RE.test(email)) return null;
        // 따옴표로 감싼 표시 이름("홍길동" <a@b.com>)도 벗겨 낸다.
        const name = m[1].replace(/^["']|["']$/g, "").trim();
        return { name: name || null, email };
    }

    return EMAIL_RE.test(raw) ? { name: null, email: raw } : null;
}

/**
 * 그룹 멤버 목록 → 입력칸 값(해당 role 만).
 *
 * 이름이 없으면 객체가 아니라 <b>문자열</b>로 돌려준다 — 객체로 주면 칩 라벨이
 * "null &lt;a@b.com&gt;" 으로 그려진다(RecipientAutocomplete 의 renderTag 가 name 을 그대로 쓴다).
 */
export function membersToValue(members, role) {
    return (members || [])
        .filter(m => (m?.role || "TO") === role)
        .map(m => (m.name ? { name: m.name, email: m.email } : m.email))
        .filter(Boolean);
}

/**
 * 입력칸 값 → 그룹 멤버 목록(role 부여).
 * 주소로 풀리지 않는 원소는 버린다 — 저장할 수 없는 값을 서버로 보내 400 을 받느니 여기서 걸러낸다.
 */
export function valueToMembers(value, role) {
    return (value || [])
        .map(toAddress)
        .filter(Boolean)
        .map(a => ({ role, name: a.name, email: a.email }));
}

/**
 * 세 칸을 한 번에 → 저장용 members 배열.
 * 순서가 받는이 → 참조 → 숨은참조인 것이 중요하다: 같은 주소가 두 칸에 들어갔을 때
 * 서버가 <b>먼저 나온 것</b>만 남기므로, 더 넓게 보이는 자리가 이긴다.
 */
export function buildMembers({ tos, ccs, bccs }) {
    return [
        ...valueToMembers(tos,  "TO"),
        ...valueToMembers(ccs,  "CC"),
        ...valueToMembers(bccs, "BCC"),
    ];
}

/** 목록 화면에서 "받는이 3 · 참조 2" 를 만드는 짧은 표기. 0 인 칸은 아예 빼서 시선을 줄인다. */
export function roleSummary(group) {
    const parts = [];
    if (group?.toCount)  parts.push(`받는이 ${group.toCount}`);
    if (group?.ccCount)  parts.push(`참조 ${group.ccCount}`);
    if (group?.bccCount) parts.push(`숨은참조 ${group.bccCount}`);
    return parts.length ? parts.join(" · ") : "비어 있음";
}
